import type {
  CreateInternalNoteBody,
  CreateInternalNoteResponse,
} from "@nlc/shared-types";
import type { UseMutationResult } from "@tanstack/react-query";

import { forwardRef, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { NoteFormValues } from "@/features/projects/project/internalNotes/types";

import { FieldError, Input, Label, TextField } from "@/components/field";
import { TiptapEditor } from "@/components/ui/TiptapEditor";
import { useUser } from "@/hooks/useUser";

import { useAutosaveNoteDraft } from "../hooks/useAutosaveNoteDraft";
import { clearNoteDraft, readNoteDraft } from "../utils/noteDraftStorage";

type CreateNoteProps = {
  createNote: UseMutationResult<
    CreateInternalNoteResponse,
    Error,
    CreateInternalNoteBody
  >;
  onSuccess?: () => void;
  requestId: number;
};

export interface CreateNoteHandle {
  submitForm: () => Promise<void>;
}

export const CreateNote = forwardRef<CreateNoteHandle, CreateNoteProps>(
  ({ createNote, onSuccess, requestId }, ref) => {
    const [editorKey, setEditorKey] = useState(0);
    const { data: user } = useUser();
    const draft = readNoteDraft(requestId, user?.user.id);
    const formMethods = useForm<NoteFormValues>({
      defaultValues: draft ?? { description: "", name: "" },
    });
    const { control, handleSubmit, reset } = formMethods;

    useAutosaveNoteDraft(requestId, user?.user.id, formMethods);

    async function handleFormSubmit(values: NoteFormValues) {
      await createNote.mutateAsync({
        description: values.description,
        name: values.name,
      });
      clearNoteDraft(requestId, user?.user.id);
      reset({ description: "", name: "" });
      setEditorKey((k) => k + 1);
      onSuccess?.();
    }

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit(handleFormSubmit),
    }));

    return (
      <form className="flex flex-col gap-xl">
        <p className="font-bold text-xl">New note</p>
        <p className="text-text-tertiary">
          Add key information about the request, as additional notes and
          reference for the designers.
        </p>
        <Controller
          control={control}
          name="name"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Name</Label>
              <Input
                data-cy="internal-notes-name"
                placeholder="Add a short note name"
                ref={ref}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
          rules={{ required: "Name is required" }}
        />

        <div className="flex flex-col gap-sm">
          <Label>Description</Label>
          <Controller
            control={control}
            key={editorKey}
            name="description"
            render={({ field }) => (
              <TiptapEditor
                className="min-h-50"
                onChangeHTML={(html) => field.onChange(html)}
                placeholder="Enter your request note for the designer"
                value={field.value}
              />
            )}
          />
        </div>
      </form>
    );
  },
);

CreateNote.displayName = "CreateNote";
